import { action, computed, observable } from 'mobx';

import { ApiUrls, SignalREvents } from '@app/AppConstants';
import { ApiV1Job, ApiV1JobList, JobMisfireInstruction, JobStatus, QueueModel, SchedulerStatus } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import { DateTimeService } from '@app/Services/DateTimeService';
import { extensions } from '@app/Services/Extensions';
import { signalRService } from '@app/Services/SignalRService';
import { MatchComparer, stringSearchService } from '@app/Services/StringSearchService';

import { isJobFailed, JobProgressMaxValue, JobProgressValue, JobStatusMessage, TriggerExtended } from '../Models/JobListModels';

const SCHEDULER_RETRY_INTERVAL_MS = 5000;
const FILTER_HIGHLIGHT_DURATION_MS = 5000;

export default class JobListStore {
    @observable jobs: ApiV1Job[] = [];
    @observable dataLoaded: boolean = false;
    @observable exceptionMessage: string = '';
    @observable jobsFilter: string = '';
    @observable showFilter: boolean = false;
    @observable queues: QueueModel[] = [];

    constructor() {
        signalRService.subscribe<JobStatusMessage>(SignalREvents.jobStatusChanged, this._onJobStatusChangedHandler);
        signalRService.subscribe<JobProgressMaxValue>(SignalREvents.jobProgressMaxValueChanged, this._onJobProgressMaxValueChangedHandler);
        signalRService.subscribe<JobProgressValue>(SignalREvents.jobProgressValueChanged, this._onJobProgressValueChangedHandler);

        this.jobsFilter = localStorage.getItem('JobsListFilter') || '';
    }

    @action.bound
    public unload() {
        signalRService.unsubscribe<JobStatusMessage>(SignalREvents.jobStatusChanged, this._onJobStatusChangedHandler);
        signalRService.unsubscribe<JobProgressMaxValue>(SignalREvents.jobProgressMaxValueChanged, this._onJobProgressMaxValueChangedHandler);
        signalRService.unsubscribe<JobProgressValue>(SignalREvents.jobProgressValueChanged, this._onJobProgressValueChangedHandler);
    }

    @action
    public setFilter(value: string) {
        this.jobsFilter = value;
        localStorage.setItem('JobsListFilter', value);
        this.showFilter = true;
        extensions.executeTimeout(this._hideFilter, FILTER_HIGHLIGHT_DURATION_MS, this);
    }

    @action
    private _hideFilter() {
        this.showFilter = false;
    }

    @computed
    get textComparer() {
        const comparator: MatchComparer = (v, t, s) => {
            return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        };
        return comparator;
    }

    @computed
    get searchTerms() {
        const searchStr = this.jobsFilter;
        return searchStr.split(/[ ,]+/).filter((x) => !!x);
    }

    @computed
    public get topExecutions() {
        return this.jobs
            .filter((j) => j.lastStatus === JobStatus.Pending)
            .map((j) => j.name ?? '')
            .slice(0, 3);
    }

    @computed
    public get topFailedJobs() {
        return this.jobs
            .filter(isJobFailed)
            .slice(0, 3);
    }

    public filterJob(job: ApiV1Job) {
        let result = true;

        if (this.jobsFilter) {
            const filters = this.jobsFilter.toLowerCase().split(';');
            const name = (job.name ?? '').toLowerCase();
            const description = job.description?.toLowerCase();
            result = false;

            for (let i = 0; i < filters.length; i++) {
                const filter = filters[i];

                let matchByFilters = true;
                const filterParts = filter.split(' ');
                for (let j = 0; j < filterParts.length; j++) {
                    let filterPart = filterParts[j];

                    const isNegative = filterPart.startsWith('-');
                    if (isNegative) {
                        filterPart = filterPart.substring(1);
                        if (name.indexOf(filterPart) !== -1 || (description && description.indexOf(filterPart) !== -1)) {
                            matchByFilters = false;
                        }
                    } else {
                        if (name.indexOf(filterPart) === -1 && (!description || description.indexOf(filterPart) === -1)) {
                            matchByFilters = false;
                        }
                    }
                }

                if (matchByFilters) {
                    result = true;
                }
            }
        }

        return result;
    }

    @action
    public updateJob(job: ApiV1Job) {
        const index = this.jobs.findIndex((x) => x.id === job.id);
        if (index !== -1 && DateTimeService.compare(this.jobs[index].timestampDateTimeUtc, job.timestampDateTimeUtc) < 0) {
            this.jobs[index] = job;
        } else if (index !== -1) {
            this.jobs[index].triggers = job.triggers;
        }
    }

    @action
    public async initJobs() {
        this.dataLoaded = false;
        this.exceptionMessage = '';
        const { data } = await ApiService.get<ApiV1JobList>(ApiUrls.JobList);

        if (data.status === SchedulerStatus.Started) {
            this.jobs = data.jobs || [];
            this.dataLoaded = true;
        } else if (data.status === SchedulerStatus.FailedToStart) {
            this.exceptionMessage = data.startupException ?? '';
            this.dataLoaded = true;
        } else {
            window.setTimeout(() => {
                void this.initJobs();
            }, SCHEDULER_RETRY_INTERVAL_MS);
        }
    }

    @action
    public async loadQueueNames() {
        const { data } = await ApiService.get<QueueModel[]>(ApiUrls.QueueNamesUrl);
        this.queues = data || [];
    }

    private _findTrigger(triggerId: string) {
        for (const job of this.jobs) {
            const trigger = job.triggers?.find(t => t.name === triggerId);
            if (trigger) return trigger as TriggerExtended;
        }
        return null;
    }

    @action.bound
    private _onJobStatusChangedHandler(_: string, jobStatus: JobStatusMessage) {
        const job = this.jobs.find((j) => j.name === jobStatus.name);
        let trigger = this._findTrigger(jobStatus.triggerId);

        if (trigger) {
            trigger.status = jobStatus.status;
            trigger.currentMessage = jobStatus.progressMessage ?? jobStatus.lastErrorMessage;
            if (jobStatus.status === JobStatus.Success) {
                trigger.scheduleError = void 0;
            }
        } else {
            trigger = {
                name: jobStatus.triggerId,
                isActive: false,
                status: jobStatus.status,
                isManual: true,
                useZrhTimeZone: false,
                misfireLogic: JobMisfireInstruction.Default
            } as TriggerExtended;
            job?.triggers?.push(trigger);
        }

        if (job && trigger.isManual) {
            const isJobStarted = jobStatus.status === JobStatus.Pending;
            const noCron = !trigger.cronSchedule;
            const hasOtherActiveTrigger = job.triggers?.find(t => t.status === JobStatus.Pending)?.name !== trigger.name;
            const shouldCleanTrigger = isJobStarted && noCron && hasOtherActiveTrigger;

            if (jobStatus.status === JobStatus.Fail || jobStatus.status === JobStatus.Success || shouldCleanTrigger) {
                job.triggers = job.triggers?.filter(x => x.name !== trigger?.name);
            }
        }

        if (job && DateTimeService.compare(job.timestampDateTimeUtc, jobStatus.timestampDateTimeUtc) < 0) {
            job.lastErrorMessage = jobStatus.lastErrorMessage;
            job.lastStatus = jobStatus.status;
            if (jobStatus.jobExecutionInfo) {
                const jobExecutionInfo = jobStatus.jobExecutionInfo;
                job.lastRunDateTimeUtc = jobExecutionInfo.lastRunDateTimeUtc;
                job.nextRunDateTimeUtc = jobExecutionInfo.nextRunDateTimeUtc;
                job.lastRunTriggerName = jobExecutionInfo.lastRunTriggerName || '';
                job.nextRunTriggerName = jobExecutionInfo.nextRunTriggerName || '';
                job.lastExecutionInfoMessage = jobExecutionInfo.lastInfoMessage || '';
            }
            job.timestampDateTimeUtc = jobStatus.timestampDateTimeUtc;
        }
    }

    @action.bound
    private _onJobProgressMaxValueChangedHandler(_: string, data: JobProgressMaxValue) {
        const trigger = this._findTrigger(data.triggerId);
        if (trigger) {
            trigger.progressMax = data.maxValue;
        }
    }

    @action.bound
    private _onJobProgressValueChangedHandler(_: string, data: JobProgressValue) {
        const trigger = this._findTrigger(data.triggerId);
        if (trigger) {
            trigger.progressMax = data.maxValue;
            trigger.progressCurrent = data.value;
        }
    }
}
